import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  TextField,
  TablePagination,
  Snackbar,
  Alert,
  LinearProgress,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import DermoMedicineForm from './CosmeticProductForm';
import ConfirmationDialog from '../components/ConfirmationDialog';

const CosmeticList = () => {
  const [medicaments, setMedicaments] = useState([]);
  const [filteredMedicaments, setFilteredMedicaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [medicamentToDelete, setMedicamentToDelete] = useState(null);
  const [open, setOpen] = useState(false);
  const [currentMedicament, setCurrentMedicament] = useState(null);
  const [searchQuery, setSearchQuery] = useState(() => {
    return sessionStorage.getItem('cosmeticsSearchQuery') || '';
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewDialog, setPreviewDialog] = useState({ open: false, data: null });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchMedicaments();
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredMedicaments(medicaments);
      return;
    }

    const filtered = medicaments.filter((med) =>
      (med.dci && med.dci.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (med.specialite && med.specialite.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (med.forme && med.forme.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    setFilteredMedicaments(filtered);
  }, [searchQuery, medicaments]);

  const fetchMedicaments = async () => {
    try {
      const response = await axios.get("cosmeticProducts", {
        headers: { 
          "x-access-token": localStorage.getItem("token") || sessionStorage.getItem('token'),
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      setMedicaments(response.data);
      setFilteredMedicaments(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };



  const handleAddClick = () => {
    setCurrentMedicament(null);
    setOpen(true);
  };

  const handleUpdateClick = (medicament) => {
    setCurrentMedicament(medicament);
    setOpen(true);
  };

  const handleDeleteClick = (id) => {
    setMedicamentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
  
    if (!medicamentToDelete) return;
  
    setMedicaments((prev) => prev.filter((med) => med._id !== medicamentToDelete));
    setFilteredMedicaments((prev) => prev.filter((med) => med._id !== medicamentToDelete));
  
    try {
      await axios.delete(`cosmeticProduct/${medicamentToDelete}`, {
        headers: {
          "x-access-token":
            localStorage.getItem("token") || sessionStorage.getItem("token"),
          Authorization: `Bearer ${
            localStorage.getItem("token") || sessionStorage.getItem("token")
          }`,
        },
      });
    } catch (error) {
      console.error("Error deleting medicament:", error);
      alert("Failed to delete medicament. Please try again.");
      fetchMedicaments();
    }
  };

 

  const handleClose = () => {
    setOpen(false);
    setCurrentMedicament(null);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    sessionStorage.setItem('cosmeticsSearchQuery', query);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleImportCosmeticsClick = () => {
    fileInputRef.current.click();
  };

  const handleCosmeticsFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['text/csv', 'application/vnd.ms-excel'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload a .csv file.');
      event.target.value = '';
      return;
    }

    if (file.size === 0) {
      alert('Cannot upload empty files.');
      event.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.');
      event.target.value = '';
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('confirm', 'false');

      setUploading(true);
      setUploadProgress(0);

      const response = await axios.post(
        'admin/cosmeticProducts/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-access-token': localStorage.getItem('token') || sessionStorage.getItem('token'),
            Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response.data.preview) {
        setSelectedFile(file);
        setPreviewDialog({ open: true, data: response.data });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'An error occurred while uploading for preview.',
        severity: 'error',
      });
      event.target.value = '';
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('confirm', 'true');

      setUploading(true);
      setPreviewDialog({ open: false, data: null });
      setUploadProgress(0);

      const response = await axios.post(
        'admin/cosmeticProducts/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-access-token': localStorage.getItem('token') || sessionStorage.getItem('token'),
            Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          },
        }
      );

      setSnackbar({
        open: true,
        message: response.data.message || 'File uploaded successfully!',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'An error occurred while uploading.',
        severity: 'error',
      });
    } finally {
      setUploading(false);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchMedicaments();
    }
  };

  return (
    <Box padding={2}>
      <Typography variant={isMobile ? "h4" : "h3"} gutterBottom>
      Cosmetic products
      </Typography>
      <Box display="flex" gap={2} mb={2}>
        <Button variant="contained" color="primary" onClick={handleAddClick}>
          Add cosmetic products
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<CloudUpload />}
          onClick={handleImportCosmeticsClick}
          disabled={uploading}
        >
          {uploading ? `Uploading ${uploadProgress}%` : 'Import CSV'}
        </Button>
        <input
          type="file"
          accept=".csv, application/vnd.ms-excel"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleCosmeticsFileChange}
        />
      </Box>
      {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />}
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchQuery}
        onChange={handleSearchChange}
      />
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: theme.palette.grey[200] }}>
              <TableRow>

                <TableCell>Specialite</TableCell>
                <TableCell>Forme</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMedicaments
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((med) => (
                  <TableRow key={med._id}>
                
                    <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word', padding: isMobile ? '6px' : 'default' }}>
                      {med.specialite}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word', padding: isMobile ? '6px' : 'default' }}>
                      {med.forme}
                    </TableCell>
                    <TableCell align="right" sx={{ padding: isMobile ? '6px' : 'default' }}>
                      <ButtonGroup variant="outlined" size="small">
                        <Button onClick={() => handleUpdateClick(med)} color="primary">
                          Update
                        </Button>
                     
                        <Button onClick={() => handleDeleteClick(med._id)} color="error">
                          Delete
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredMedicaments.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message="Are you sure you want to delete this medicament?"
        confirmText="Delete"
        confirmColor="error"
      />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{currentMedicament ? 'Update Medicament' : 'Add Medicament'}</DialogTitle>
        <DialogContent>
          <DermoMedicineForm
            medicament={currentMedicament}
            onClose={handleClose}
            onSave={fetchMedicaments}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* CSV Preview Recap Dialog */}
      <Dialog 
        open={previewDialog.open} 
        onClose={() => { setPreviewDialog({ open: false, data: null }); setSelectedFile(null); if(fileInputRef.current) fileInputRef.current.value = ''; }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>CSV Import Recap</DialogTitle>
        <DialogContent dividers>
          {previewDialog.data && (
            <>
              <Typography variant="h6" color="primary" gutterBottom>
                Products to ADD: {previewDialog.data.toInsert?.length || 0}
              </Typography>
              {previewDialog.data.toInsert && previewDialog.data.toInsert.length > 0 && (
                <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 3 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Specialite</TableCell>
                        <TableCell>Forme</TableCell>
                        <TableCell>Code Barre</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewDialog.data.toInsert.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.specialite}</TableCell>
                          <TableCell>{item.forme || 'N/A'}</TableCell>
                          <TableCell>{item.code_barre}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Typography variant="h6" color="warning" gutterBottom>
                Products to MODIFY: {previewDialog.data.toModify?.length || 0}
              </Typography>
              {previewDialog.data.toModify && previewDialog.data.toModify.length > 0 && (
                <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 3 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Specialite</TableCell>
                        <TableCell>Forme</TableCell>
                        <TableCell>Code Barre</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewDialog.data.toModify.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.specialite}</TableCell>
                          <TableCell>{item.forme || 'N/A'}</TableCell>
                          <TableCell>{item.code_barre}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Typography variant="h6" color="error" gutterBottom>
                Products to SKIP: {previewDialog.data.toSkip?.length || 0}
              </Typography>
              {previewDialog.data.toSkip && previewDialog.data.toSkip.length > 0 && (
                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Specialite</TableCell>
                        <TableCell>Code Barre</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewDialog.data.toSkip.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.title}</TableCell>
                          <TableCell>{item.barcode}</TableCell>
                          <TableCell>{item.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {((previewDialog.data.toInsert?.length || 0) === 0 && (previewDialog.data.toModify?.length || 0) === 0) && (
                <Typography color="textSecondary" sx={{ mt: 2 }}>
                  Nothing new to add or modify. All items in the CSV appear to be invalid.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => { setPreviewDialog({ open: false, data: null }); setSelectedFile(null); if(fileInputRef.current) fileInputRef.current.value = ''; }} 
            color="secondary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmUpload} 
            variant="contained" 
            color="primary"
            disabled={((previewDialog.data?.toInsert?.length || 0) === 0 && (previewDialog.data?.toModify?.length || 0) === 0) || uploading}
          >
            Confirm Import ({ (previewDialog.data?.toInsert?.length || 0) + (previewDialog.data?.toModify?.length || 0) })
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CosmeticList;